import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { CopyTrackToClipboard } from '../../../../utilities/BL/Admin/Overlays/Playlist';
import { MESSAGE_TYPE } from '../../../Providers/FloatMessage';
import { PlaylistEditSongModal } from './PlaylistEditSongModal';

/**
 * COMPONENTE: PlaylistSuggestionsTable (Bandeja de Entrada de Cabina / DJ)
 * 
 * Panel izquierdo de la Consola de Sonido para [HU-05] Arma la Playlist.
 * Permite filtrar, buscar, agrupar y gestionar sugerencias musicales de aficionados
 * y canciones manuales del operador.
 */
export const PlaylistSuggestionsTable = ({
    suggestions = [],
    activePlaylistIds = [],
    nowPlayingId = null,
    onToggleFavorite = () => {},
    onToggleArchive = () => {},
    onDeleteSuggestion = () => {},
    onOpenManualModal = () => {},
    onSuggestionUpdated = () => {},
    onOpenEditModal = null,
    roomId = null,
    showMessage = () => {},
    isLoading = false
}) => {
    // -------------------------------------------------------------------------
    // 1. ESTADOS LOCALES DE FILTRADO, BÚSQUEDA Y AGRUPACIÓN
    // -------------------------------------------------------------------------
    const [searchQuery, setSearchQuery] = useState('');
    const [isGrouped, setIsGrouped] = useState(false);
    const [activeTab, setActiveTab] = useState('all'); // 'all', 'songs', 'artists', 'manual', 'archived'
    const [sortBy, setSortBy] = useState('recent'); // 'recent', 'votes', 'title'
    const [expandedKeys, setExpandedKeys] = useState({}); // Mapa de grupos expandidos { [normalizedKey]: true/false }
    const [editingSuggestion, setEditingSuggestion] = useState(null);

    // -------------------------------------------------------------------------
    // 2. FORMATEO DE TIEMPO
    // -------------------------------------------------------------------------
    const formatRelativeTime = (dateString) => {
        if (!dateString) return 'Reciente';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Reciente';
            
            const now = new Date();
            const diffSeconds = Math.floor((now - date) / 1000);
            
            if (diffSeconds < 30) return 'Hace un momento';
            if (diffSeconds < 60) return `Hace ${diffSeconds}s`;
            const diffMinutes = Math.floor(diffSeconds / 60);
            if (diffMinutes < 60) return `Hace ${diffMinutes}m`;
            const diffHours = Math.floor(diffMinutes / 60);
            if (diffHours < 24) return `Hace ${diffHours}h`;
            
            return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false }) + ' hrs';
        } catch {
            return 'Reciente';
        }
    };

    const formatFullDateTime = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return date.toLocaleString('es-MX', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            });
        } catch {
            return '';
        }
    };

    // -------------------------------------------------------------------------
    // 3. ACCIONES DE COPIA RÁPIDA
    // -------------------------------------------------------------------------
    const handleCopy = async (title, artist) => {
        const result = await CopyTrackToClipboard(title, artist);
        if (result.success) {
            showMessage({
                type: MESSAGE_TYPE.SUCCESS,
                text: `📋 Copiado: "${result.text}"`
            });
        } else {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: result.message || 'No se pudo copiar la pista.'
            });
        }
    };

    // Alternar expansión de grupo
    const toggleExpandGroup = (key) => {
        setExpandedKeys(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    // -------------------------------------------------------------------------
    // 4. FILTRADO Y PROCESAMIENTO REACTIVO DE SUGERENCIAS
    // -------------------------------------------------------------------------
    const filteredSuggestions = useMemo(() => {
        if (!Array.isArray(suggestions)) return [];

        return suggestions.filter(item => {
            if (!item) return false;

            // Filtro de archivados vs no archivados
            if (activeTab === 'archived') {
                if (!item.isArchived) return false;
            } else {
                if (item.isArchived) return false;
            }

            // Filtro por pestaña
            if (activeTab === 'songs') {
                // Tipo 1 = Canción o 0 por defecto
                if (item.suggestionType === 2) return false;
            } else if (activeTab === 'artists') {
                if (item.suggestionType !== 2) return false;
            } else if (activeTab === 'manual') {
                if (!item.isManualEntry && !item.createdByOperatorName) return false;
            }

            // Filtro por texto de búsqueda
            if (searchQuery.trim()) {
                const query = searchQuery.toLowerCase().trim();
                const title = (item.title || '').toLowerCase();
                const artist = (item.artistName || '').toLowerCase();
                const fan = (item.fanName || '').toLowerCase();
                const operator = (item.createdByOperatorName || '').toLowerCase();

                const matchTitle = title.includes(query);
                const matchArtist = artist.includes(query);
                const matchFan = fan.includes(query);
                const matchOperator = operator.includes(query);

                if (!matchTitle && !matchArtist && !matchFan && !matchOperator) {
                    return false;
                }
            }

            return true;
        });
    }, [suggestions, activeTab, searchQuery]);

    // -------------------------------------------------------------------------
    // 5. AGRUPACIÓN Y ORDENAMIENTO
    // -------------------------------------------------------------------------
    const processedData = useMemo(() => {
        if (!isGrouped) {
            // Vista Plana (Sin Agrupar)
            const sorted = [...filteredSuggestions];
            sorted.sort((a, b) => {
                if (sortBy === 'votes') {
                    return (b.voteCount || 0) - (a.voteCount || 0);
                }
                if (sortBy === 'title') {
                    return (a.title || '').localeCompare(b.title || '');
                }
                // Por defecto: Más recientes
                const dateA = new Date(a.createdAtUtc || a.createdAt || 0).getTime();
                const dateB = new Date(b.createdAtUtc || b.createdAt || 0).getTime();
                return dateB - dateA;
            });
            return { isGroupedView: false, items: sorted };
        }

        // Vista Agrupada por Canción / normalizedKey
        const groupsMap = new Map();

        filteredSuggestions.forEach(item => {
            const cleanTitle = (item.title || '').trim().toLowerCase();
            const cleanArtist = (item.artistName || '').trim().toLowerCase();
            const groupKey = item.normalizedKey || `${cleanTitle}|${cleanArtist}`;

            if (!groupsMap.has(groupKey)) {
                groupsMap.set(groupKey, {
                    key: groupKey,
                    normalizedKey: groupKey,
                    primaryItem: item, // Elemento representativo (con id principal)
                    title: item.title,
                    artistName: item.artistName || '',
                    suggestionType: item.suggestionType,
                    isManualEntry: item.isManualEntry,
                    totalVotes: 0,
                    requestCount: 0,
                    isFavorite: false,
                    isNowPlaying: false,
                    isPlayed: false,
                    isArchived: item.isArchived,
                    requests: []
                });
            }

            const group = groupsMap.get(groupKey);
            group.requestCount += 1;
            group.totalVotes += (item.voteCount && item.voteCount > 0 ? item.voteCount : 1);
            if (item.isFavorite) group.isFavorite = true;
            if (item.isNowPlaying) group.isNowPlaying = true;
            if (item.isPlayed) group.isPlayed = true;

            group.requests.push({
                id: item.id,
                fanName: item.fanName || (item.createdByOperatorName ? `Operador: ${item.createdByOperatorName}` : 'Aficionado'),
                fanSessionId: item.fanSessionId,
                createdAtUtc: item.createdAtUtc || item.createdAt,
                voteCount: item.voteCount || 1,
                isManual: Boolean(item.isManualEntry || item.createdByOperatorName),
                originalItem: item
            });
        });

        const groupsList = Array.from(groupsMap.values());

        // Ordenar grupos
        groupsList.sort((a, b) => {
            if (sortBy === 'votes') {
                return b.totalVotes - a.totalVotes;
            }
            if (sortBy === 'title') {
                return (a.title || '').localeCompare(b.title || '');
            }
            // Recientes: ordenar por la petición más reciente del grupo
            const latestA = Math.max(...a.requests.map(r => new Date(r.createdAtUtc || 0).getTime()));
            const latestB = Math.max(...b.requests.map(r => new Date(r.createdAtUtc || 0).getTime()));
            return latestB - latestA;
        });

        return { isGroupedView: true, items: groupsList };
    }, [filteredSuggestions, isGrouped, sortBy]);

    // -------------------------------------------------------------------------
    // 6. RENDERIZADO
    // -------------------------------------------------------------------------
    return (
        <div className="DJBoard__Panel DJBoard__Panel--Inbox">
            {/* --- CABECERA DEL PANEL IZQUIERDO --- */}
            <div className="DJBoard__PanelHeader">
                <div className="DJBoard__PanelHeader__TitleRow">
                    <div className="d-flex align-items-center gap-2">
                        <span className="DJBoard__PanelHeader__Icon">📥</span>
                        <h2 className="DJBoard__PanelHeader__Title">Bandeja de Sugerencias</h2>
                    </div>
                    <span className="DJBoard__PanelHeader__CountBadge">
                        {filteredSuggestions.length} {filteredSuggestions.length === 1 ? 'pista' : 'pistas'}
                    </span>
                </div>

                {/* --- CONTROLES Y BUSCADOR --- */}
                <div className="DJBoard__PanelHeader__Controls">
                    {/* Buscador en Vivo */}
                    <div className="DJBoard__SearchBox">
                        <i className="bi bi-search DJBoard__SearchBox__Icon"></i>
                        <input
                            type="text"
                            className="DJBoard__SearchBox__Input"
                            placeholder="Buscar por canción, artista o aficionado..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                className="DJBoard__SearchBox__ClearBtn"
                                onClick={() => setSearchQuery('')}
                                title="Limpiar búsqueda"
                            >
                                <i className="bi bi-x-circle-fill"></i>
                            </button>
                        )}
                    </div>

                    {/* Switch: Mostrar Todo vs Agrupar */}
                    <div className="DJBoard__ViewToggle">
                        <span className={`DJBoard__ViewToggle__Label ${!isGrouped ? 'is-active' : ''}`}>
                            Mostrar Todo
                        </span>
                        <div className="form-check form-switch m-0 p-0 d-flex align-items-center">
                            <input
                                className="form-check-input DJBoard__ToggleSwitch"
                                type="checkbox"
                                role="switch"
                                id="switchGroupSimilar"
                                checked={isGrouped}
                                onChange={(e) => setIsGrouped(e.target.checked)}
                            />
                        </div>
                        <span className={`DJBoard__ViewToggle__Label ${isGrouped ? 'is-active' : ''}`}>
                            Agrupar por Canción 🗂️
                        </span>
                    </div>
                </div>

                {/* --- PESTAÑAS (TABS) Y ORDENAMIENTO --- */}
                <div className="DJBoard__FiltersRow">
                    {/* Tabs */}
                    <div className="DJBoard__Tabs">
                        <button
                            type="button"
                            className={`DJBoard__Tabs__Item ${activeTab === 'all' ? 'is-active' : ''}`}
                            onClick={() => setActiveTab('all')}
                        >
                            Todas
                        </button>
                        <button
                            type="button"
                            className={`DJBoard__Tabs__Item ${activeTab === 'songs' ? 'is-active' : ''}`}
                            onClick={() => setActiveTab('songs')}
                        >
                            Canciones 🎵
                        </button>
                        <button
                            type="button"
                            className={`DJBoard__Tabs__Item ${activeTab === 'artists' ? 'is-active' : ''}`}
                            onClick={() => setActiveTab('artists')}
                        >
                            Artistas 🎤
                        </button>
                        <button
                            type="button"
                            className={`DJBoard__Tabs__Item ${activeTab === 'manual' ? 'is-active' : ''}`}
                            onClick={() => setActiveTab('manual')}
                        >
                            Manuales 🎧
                        </button>
                        <button
                            type="button"
                            className={`DJBoard__Tabs__Item ${activeTab === 'archived' ? 'is-active' : ''}`}
                            onClick={() => setActiveTab('archived')}
                        >
                            Archivadas 📦
                        </button>
                    </div>

                    {/* Selector de Orden */}
                    <div className="DJBoard__SortSelectWrapper">
                        <label className="DJBoard__SortSelect__Label" htmlFor="selectSortBy">
                            <i className="bi bi-filter"></i> Orden:
                        </label>
                        <select
                            id="selectSortBy"
                            className="DJBoard__SortSelect"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            <option value="recent">Más Recientes ⏱️</option>
                            <option value="votes">Más Votadas 🔥</option>
                            <option value="title">Título (A-Z) 🔤</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* --- BOTÓN DE CREACIÓN MANUAL RÁPIDA --- */}
            <div className="DJBoard__QuickAddBanner">
                <div className="DJBoard__QuickAddBanner__Text">
                    <span className="fw-bold">🎧 ¿Quieres agregar una pista personalizada?</span>
                    <span className="small text-muted">Añade canciones manuales directamente a la cola o bandeja.</span>
                </div>
                <button
                    type="button"
                    className="DJBoard__QuickAddBanner__Button"
                    onClick={onOpenManualModal}
                >
                    <i className="bi bi-plus-circle-fill me-1"></i> Añadir Canción
                </button>
            </div>

            {/* --- LISTADO DE SUGERENCIAS (SCROLL INDEPENDIENTE) --- */}
            <div className="DJBoard__PanelBody">
                {isLoading && suggestions.length === 0 ? (
                    <div className="DJBoard__EmptyState">
                        <div className="spinner-border text-danger mb-3" role="status"></div>
                        <p className="DJBoard__EmptyState__Text">Cargando sugerencias de la cabina...</p>
                    </div>
                ) : processedData.items.length === 0 ? (
                    <div className="DJBoard__EmptyState">
                        <i className="bi bi-music-note-beamed DJBoard__EmptyState__Icon"></i>
                        <p className="DJBoard__EmptyState__Text">
                            {searchQuery
                                ? 'No se encontraron pistas que coincidan con la búsqueda.'
                                : activeTab === 'archived'
                                ? 'No hay sugerencias archivadas.'
                                : 'No hay sugerencias en esta sección todavía.'}
                        </p>
                        {searchQuery && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-light mt-2"
                                onClick={() => setSearchQuery('')}
                            >
                                Limpiar filtro
                            </button>
                        )}
                    </div>
                ) : processedData.isGroupedView ? (
                    // =========================================================
                    // MODO AGRUPADO POR CANCIÓN
                    // =========================================================
                    <div className="DJBoard__List">
                        {processedData.items.map((group) => {
                            const isExpanded = Boolean(expandedKeys[group.key]);
                            const isInActiveQueue = activePlaylistIds.includes(group.primaryItem.id) || group.isFavorite;
                            const isLive = nowPlayingId === group.primaryItem.id || group.isNowPlaying;
                            const hasPopularBadge = group.requestCount > 1 || group.totalVotes > 1;

                            return (
                                <div
                                    key={group.key}
                                    className={`DJBoard__Card DJBoard__Card--Grouped ${isInActiveQueue ? 'is-in-queue' : ''} ${isLive ? 'is-now-playing' : ''}`}
                                >
                                    {/* Cabecera de la tarjeta agrupada */}
                                    <div className="DJBoard__Card__MainRow">
                                        <div className="DJBoard__Card__TrackInfo">
                                            <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                                                <span className="DJBoard__TrackTypeBadge">
                                                    {group.suggestionType === 2 ? '🎤 Artista' : '🎵 Canción'}
                                                </span>

                                                {hasPopularBadge && (
                                                    <span className="DJBoard__PopularityBadge" title={`${group.requestCount} aficionados la pidieron`}>
                                                        🔥 Pedida {group.requestCount} {group.requestCount === 1 ? 'vez' : 'veces'}
                                                        {group.totalVotes > group.requestCount && ` (${group.totalVotes} votos)`}
                                                    </span>
                                                )}

                                                {isInActiveQueue && (
                                                    <span className="DJBoard__InQueueBadge">
                                                        ⭐ En Playlist Activa
                                                    </span>
                                                )}

                                                {isLive && (
                                                    <span className="DJBoard__LiveBadge">
                                                        <span className="DJBoard__LiveDot"></span> EN VIVO
                                                    </span>
                                                )}
                                            </div>

                                            <h3 className="DJBoard__Card__Title">{group.title}</h3>
                                            <p className="DJBoard__Card__Artist">
                                                {group.artistName ? group.artistName : <span className="text-muted fst-italic">Artista no especificado</span>}
                                            </p>

                                            <div className="DJBoard__Card__Meta">
                                                <span>
                                                    <i className="bi bi-people-fill me-1"></i>
                                                    {group.requests.length} {group.requests.length === 1 ? 'solicitud' : 'solicitudes'}
                                                </span>
                                                <span>•</span>
                                                <span>Última: {formatRelativeTime(group.requests[0]?.createdAtUtc)}</span>
                                            </div>
                                        </div>

                                        {/* Acciones de la Tarjeta Agrupada */}
                                        <div className="DJBoard__Card__Actions">
                                            {/* Favorito / Pin a Playlist Activa */}
                                            <button
                                                type="button"
                                                className={`DJBoard__ActionBtn DJBoard__ActionBtn--Favorite ${isInActiveQueue ? 'is-active' : ''}`}
                                                onClick={() => onToggleFavorite(group.primaryItem.id, !isInActiveQueue)}
                                                title={isInActiveQueue ? 'Quitar de la Playlist Activa' : 'Agregar a Playlist Activa'}
                                            >
                                                <i className={`bi ${isInActiveQueue ? 'bi-star-fill text-warning' : 'bi-star'}`}></i>
                                            </button>

                                            {/* Copiar Pista */}
                                            <button
                                                type="button"
                                                className="DJBoard__ActionBtn DJBoard__ActionBtn--Copy"
                                                onClick={() => handleCopy(group.title, group.artistName)}
                                                title="Copiar 'Título - Artista' al portapapeles"
                                            >
                                                <i className="bi bi-clipboard"></i>
                                            </button>

                                            {/* Editar / Corregir Pista */}
                                            <button
                                                type="button"
                                                className="DJBoard__ActionBtn DJBoard__ActionBtn--Edit"
                                                onClick={() => onOpenEditModal ? onOpenEditModal(group.primaryItem) : setEditingSuggestion(group.primaryItem)}
                                                title="Corregir ortografía o editar sugerencia"
                                            >
                                                <i className="bi bi-pencil"></i>
                                            </button>

                                            {/* Archivar / Desarchivar */}
                                            <button
                                                type="button"
                                                className="DJBoard__ActionBtn DJBoard__ActionBtn--Archive"
                                                onClick={() => onToggleArchive(group.primaryItem.id, !group.isArchived)}
                                                title={group.isArchived ? 'Desarchivar sugerencia' : 'Archivar sugerencia'}
                                            >
                                                <i className={`bi ${group.isArchived ? 'bi-box-arrow-up' : 'bi-archive'}`}></i>
                                            </button>

                                            {/* Expandir lista de aficionados */}
                                            <button
                                                type="button"
                                                className={`DJBoard__ActionBtn DJBoard__ActionBtn--Expand ${isExpanded ? 'is-expanded' : ''}`}
                                                onClick={() => toggleExpandGroup(group.key)}
                                                title={isExpanded ? 'Colapsar detalles' : 'Ver aficionados que la solicitaron'}
                                            >
                                                <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Sección Desplegable: Aficionados que solicitaron la pista */}
                                    {isExpanded && (
                                        <div className="DJBoard__GroupAccordion">
                                            <div className="DJBoard__GroupAccordion__Header">
                                                <span className="small fw-bold text-muted">Historial de solicitudes para este tema:</span>
                                            </div>
                                            <div className="DJBoard__GroupAccordion__List">
                                                {group.requests.map((req, idx) => (
                                                    <div key={req.id || idx} className="DJBoard__GroupAccordion__Item">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span className="DJBoard__GroupAccordion__Index">#{idx + 1}</span>
                                                            <span className="DJBoard__GroupAccordion__Fan">
                                                                <i className={`bi ${req.isManual ? 'bi-headset text-info' : 'bi-person-fill text-muted'} me-1`}></i>
                                                                {req.fanName}
                                                            </span>
                                                        </div>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span className="DJBoard__GroupAccordion__Time">
                                                                {formatFullDateTime(req.createdAtUtc)} ({formatRelativeTime(req.createdAtUtc)})
                                                            </span>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-link text-danger p-0 ms-1"
                                                                onClick={() => onDeleteSuggestion(req.id, group.title)}
                                                                title="Eliminar esta solicitud específica"
                                                            >
                                                                <i className="bi bi-trash"></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    // =========================================================
                    // MODO PLANO / INDIVIDUAL
                    // =========================================================
                    <div className="DJBoard__List">
                        {processedData.items.map((item) => {
                            const isInActiveQueue = activePlaylistIds.includes(item.id) || item.isFavorite;
                            const isLive = nowPlayingId === item.id || item.isNowPlaying;
                            const isManual = item.isManualEntry || Boolean(item.createdByOperatorName);
                            const hasPopularBadge = item.voteCount && item.voteCount > 1;

                            return (
                                <div
                                    key={item.id}
                                    className={`DJBoard__Card ${isInActiveQueue ? 'is-in-queue' : ''} ${isLive ? 'is-now-playing' : ''} ${item.isPlayed ? 'is-played' : ''}`}
                                >
                                    <div className="DJBoard__Card__MainRow">
                                        <div className="DJBoard__Card__TrackInfo">
                                            <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                                                <span className="DJBoard__TrackTypeBadge">
                                                    {isManual
                                                        ? '🎧 Manual DJ'
                                                        : item.suggestionType === 2
                                                        ? '🎤 Artista'
                                                        : '🎵 Canción'}
                                                </span>

                                                {hasPopularBadge && (
                                                    <span className="DJBoard__PopularityBadge">
                                                        🔥 {item.voteCount} votos
                                                    </span>
                                                )}

                                                {isInActiveQueue && (
                                                    <span className="DJBoard__InQueueBadge">
                                                        ⭐ En Playlist Activa
                                                    </span>
                                                )}

                                                {isLive && (
                                                    <span className="DJBoard__LiveBadge">
                                                        <span className="DJBoard__LiveDot"></span> EN VIVO
                                                    </span>
                                                )}

                                                {item.isPlayed && (
                                                    <span className="DJBoard__PlayedBadge">
                                                        <i className="bi bi-check2-all me-1"></i> Reproducida
                                                    </span>
                                                )}
                                            </div>

                                            <h3 className="DJBoard__Card__Title">{item.title}</h3>
                                            <p className="DJBoard__Card__Artist">
                                                {item.artistName ? item.artistName : <span className="text-muted fst-italic">Artista no especificado</span>}
                                            </p>

                                            <div className="DJBoard__Card__Meta">
                                                <span>
                                                    <i className={`bi ${isManual ? 'bi-headset text-info' : 'bi-person text-muted'} me-1`}></i>
                                                    {item.fanName || item.createdByOperatorName || 'Aficionado anónimo'}
                                                </span>
                                                <span>•</span>
                                                <span>{formatRelativeTime(item.createdAtUtc || item.createdAt)}</span>
                                            </div>
                                        </div>

                                        {/* Acciones de la Tarjeta Individual */}
                                        <div className="DJBoard__Card__Actions">
                                            {/* Favorito / Pin a Playlist Activa */}
                                            <button
                                                type="button"
                                                className={`DJBoard__ActionBtn DJBoard__ActionBtn--Favorite ${isInActiveQueue ? 'is-active' : ''}`}
                                                onClick={() => onToggleFavorite(item.id, !isInActiveQueue)}
                                                title={isInActiveQueue ? 'Quitar de la Playlist Activa' : 'Agregar a Playlist Activa'}
                                            >
                                                <i className={`bi ${isInActiveQueue ? 'bi-star-fill text-warning' : 'bi-star'}`}></i>
                                            </button>

                                            {/* Copiar Pista */}
                                            <button
                                                type="button"
                                                className="DJBoard__ActionBtn DJBoard__ActionBtn--Copy"
                                                onClick={() => handleCopy(item.title, item.artistName)}
                                                title="Copiar 'Título - Artista' al portapapeles"
                                            >
                                                <i className="bi bi-clipboard"></i>
                                            </button>

                                            {/* Editar / Corregir Pista */}
                                            <button
                                                type="button"
                                                className="DJBoard__ActionBtn DJBoard__ActionBtn--Edit"
                                                onClick={() => onOpenEditModal ? onOpenEditModal(item) : setEditingSuggestion(item)}
                                                title="Corregir ortografía o editar sugerencia"
                                            >
                                                <i className="bi bi-pencil"></i>
                                            </button>

                                            {/* Archivar / Desarchivar */}
                                            <button
                                                type="button"
                                                className="DJBoard__ActionBtn DJBoard__ActionBtn--Archive"
                                                onClick={() => onToggleArchive(item.id, !item.isArchived)}
                                                title={item.isArchived ? 'Desarchivar sugerencia' : 'Archivar sugerencia'}
                                            >
                                                <i className={`bi ${item.isArchived ? 'bi-box-arrow-up' : 'bi-archive'}`}></i>
                                            </button>

                                            {/* Eliminar Sugerencia */}
                                            <button
                                                type="button"
                                                className="DJBoard__ActionBtn DJBoard__ActionBtn--Delete"
                                                onClick={() => onDeleteSuggestion(item.id, item.title)}
                                                title="Eliminar sugerencia"
                                            >
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Modal de Edición / Corrección Ortográfica */}
            {editingSuggestion && (
                <PlaylistEditSongModal
                    suggestionData={editingSuggestion}
                    onClose={() => setEditingSuggestion(null)}
                    onSuccess={(updated) => {
                        setEditingSuggestion(null);
                        if (typeof onSuggestionUpdated === 'function') {
                            onSuggestionUpdated(updated);
                        }
                    }}
                    roomId={roomId}
                />
            )}
        </div>
    );
};

PlaylistSuggestionsTable.propTypes = {
    suggestions: PropTypes.array,
    activePlaylistIds: PropTypes.array,
    nowPlayingId: PropTypes.string,
    onToggleFavorite: PropTypes.func,
    onToggleArchive: PropTypes.func,
    onDeleteSuggestion: PropTypes.func,
    onOpenManualModal: PropTypes.func,
    onSuggestionUpdated: PropTypes.func,
    onOpenEditModal: PropTypes.func,
    roomId: PropTypes.string,
    showMessage: PropTypes.func,
    isLoading: PropTypes.bool
};

export default PlaylistSuggestionsTable;
